// AI Generated
import { Component, Input, OnInit } from '@angular/core';
import { LabelRepo } from '@monsido/modules/endpoints/api/label.repo';
import { PageService } from '@client/app/services/entities/page/page.service';
import { SessionService } from '@monsido/core/session/session.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SpellingToolRepoService } from '../../../services/api/spelling-tool-repo/spelling-tool-repo.service';
import { DemoHasAccessService } from '../../../modules/demodata/services/demo-has-access.service';
import { LoginService } from '@client/app/core/services/login/login.service';
import { PipeService } from '../../../filters/pipe-transformation.service';
import { safeClone } from '@client/app/shared/utils/object-utils';
import { LabelTableService } from '@client/app/services/label-table/label-table.service';

@Component({
    selector: 'document-details',
    standalone: false,
    template: `
        <div class="document-details-view d-flex flex-column">
            <div class="pl-12 pr-9 pb-3 d-flex align-items-center document-details-heading">
                <div class="ml-2 mr-auto document-details-title">
                    <div class="text-ellipsis">
                        <h1 data-test="document-details-heading" class="mt-0 mb-1 text-ellipsis">{{page?.title || '(' + ('No title found' | translate) + ')' }}</h1>
                        <a [href]="page?.url" target="_blank" aria-describedby="linkOpensInNewTab" class="text-nowrap text-ellipsis">
                            <span class="fas fa-external-link-alt" aria-hidden="true"></span>
                            {{ page?.url }}
                        </a>
                    </div>
                    <label-container [currentLabels]="page?.labels"
                                     class="inline-block pt-1"
                                     [attr.aria-label]="'Labels' | translate"
                                     (getLabels)="getLabels()"
                                     [allLabels]="allLabels"
                                     (onUpdateLabel)="updatePage(page)"
                                     [labelsNoBeforeMinimize]="7"
                                     [allowEdit]="labelTableService.canAssignLabels">
                    </label-container>
                </div>
            </div>
            <div class="document-details-topmenu">
                <ul>
                    <ng-container *ngFor="let link of links">
                        <li [ngClass]="{'active': link === active, 'mon-disabled': !canSeePage(link)}"
                            (click)="canSeePage(link) && (active = link)"
                            *ngIf="!link.hasOwnProperty('show') || link.show"
                            [attr.aria-label]="link.name"
                            class="cursor-pointer">
                            <span [ngClass]="link.icon" aria-hidden="true"></span>&nbsp;
                            <span class="visible-lg-inline" [innerHTML]="link.name"></span>
                            <span class="text-grey ml-1">{{ link.count }}</span>
                        </li>
                    </ng-container>
                </ul>
            </div>
            <div class="flex-grow-1 row d-flex align-items-stretch">
                <div class="col-48">
                    <div class="page-details-section" [ngClass]="{'bg-light-grey': active?.module === null}">
                        <div class="alert alert-info mb-0" *ngIf="origPage | isUsingDemoData" translate>
                            You are currently using Demo Data, certain features will be unavailable
                        </div>
                        <document-details-section-dashboard
                                                        (updatePage)="updatePageHandler($event)"
                                                        (callback)="goToSectionHandler($event)"
                                                        [page]="page"
                                                        [document]="document"
                                                        [languages]="languages"
                                                        *ngIf="active?.module === null && !loading">
                        </document-details-section-dashboard>
                        <page-details-section-policies [page]="page"
                                                       [active]="subTarget"
                                                       (refreshPage)="refreshPageHandler($event)"
                                                       *ngIf="active?.module === 'policies' && ('global_policy' | activeFeature)">
                        </page-details-section-policies>
                        <page-details-section-qa [page]="page"
                                                 [active]="subTarget"
                                                 (refreshPage)="refreshPageHandler($event)"
                                                 *ngIf="active?.module === 'qa'">
                        </page-details-section-qa>
                        <document-details-section-accessibility [page]="page"
                                                                [document]="document"
                                                                *ngIf="active?.module === 'accessibility' && hasPdfAccessibilityAccess()"
                                                                (updateDocument)="setDocumentHandler($event)">
                        </document-details-section-accessibility>
                        <page-details-section-accessibility [page]="page"
                                                            *ngIf="active?.module === 'accessibility' && !hasPdfAccessibilityAccess()"
                                                            (refreshPage)="refreshPageHandler($event)">
                        </page-details-section-accessibility>
                        <page-details-section-inventory [page]="page"
                                                        [active]="subTarget"
                                                        *ngIf="active?.module === 'inventory'">
                        </page-details-section-inventory>
                        <page-details-section-data-privacy [pageObj]="page"
                                                           [active]="subTarget"
                                                           *ngIf="active?.module === 'data-privacy'"
                                                           (refreshPage)="refreshPage($event.target)">
                        </page-details-section-data-privacy>
                    </div>
                </div>
            </div>
        </div>
    `,
})
export class DocumentDetailsComponent implements OnInit {
    @Input() document: any;
    @Input() target: any;
    @Input() subTarget: any;

    origPage: any = null;
    page: any = null;
    links: any[] = [];
    languages: any[] = [];
    isAdmin: boolean = false;
    allLabels: any[] = [];
    active: any;
    loading = false;

    updatePageHandler = (page: any) => this.updatePage(page);
    goToSectionHandler = (params: any) => this.goToSection(params);
    refreshPageHandler = (target: any) => this.refreshPage(target);
    setDocumentHandler = (document: any) => this.setDocument(document);

    constructor (
        private labelRepo: LabelRepo,
        private pageService: PageService,
        private sessionService: SessionService,
        private translateService: TranslateService,
        private spellingToolRepoService: SpellingToolRepoService,
        private demoHasAccessService: DemoHasAccessService,
        private loginService: LoginService,
        private pipeService: PipeService,
        public labelTableService: LabelTableService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    activate (): void {
        this.origPage = null;
        this.page = null;
        this.target = this.target || null;
        this.subTarget = this.subTarget || null;
        this.links = [];
        this.languages = [];
        this.isAdmin = this.sessionService.hasRole('customer_admin');

        this.getLabels();
        this.getLanguages();
        this.getPage(true);
    }

    getPage (runSetupActive: boolean): void {
        this.loading = true;
        this.pageService.get(this.document).then((page: any) => {
            this.page = page;
            this.origPage = safeClone(page);
            this.setupLinks(runSetupActive);
            this.loading = false;
        }).catch(() => {
            this.loading = false;
        });
    }

    refreshPage (target: any): void {
        if (target.target) {
            this.target = target.target;
        } else {
            this.target = target;
        }
        this.getPage(true);
    }

    getLabels (): void {
        this.labelRepo.getLabels().then((data: any) => {
            this.allLabels = data;
        }).catch(() => {});
    }

    updatePage (page: any): void {
        const params = {
            language: page.language,
            labels: page.labels.map((label: any) => {
                return label.id;
            }),
        };

        this.pageService.update(this.origPage, params);
    }

    setDocument (document: any): void {
        this.document = document;
    }

    getTitle (): string {
        if (!this.page || !this.page.title) {
            return this.translateService.getString('Unknown title');
        }

        return this.page.title;
    }

    canSeePage (link: any): boolean {
        if (this.demoHasAccessService.isUsingDemoData(this.origPage)) {
            return link.link === 'dashboard' || link.link === 'accessibility';
        }

        return true;
    }

    goToSection (params: any): void {
        this.target = params.target;
        this.subTarget = params.subTarget;
        this.setupActive();
    }

    hasPdfAccessibilityAccess (): boolean {
        const hasPdfAccessibility = Boolean(this.sessionService.customer && this.sessionService.customer.plan_traits.clarity_pdf_accessibility);
        return this.demoHasAccessService.canAccessAccessibilityPDF() || hasPdfAccessibility;
    }

    // PROTECTED

    setupActive (): void {
        let selected = null;
        if (this.target) {
            for (let i = 0; i < this.links.length; i++) {
                if (this.links[i].module === this.target || 'page-details-section-' + this.links[i].module === this.target) {
                    selected = this.links[i];
                    break;
                }
            }
        }

        this.active = selected || this.links[0];
    }

    getLanguages (): void {
        this.spellingToolRepoService.get().then((data: any) => {
            this.languages = data;
        }).catch(() => {});
    }

    setupLinks (runSetupActive: boolean): void {
        this.loginService.getUser().then((user: any) => {
            user = user === null ? this.sessionService.me : user;
            this.links = [];
            const MON_MODULES = {
                policies: 'policies',
                qa: 'qa',
                accessibility: 'accessibility',
                inventory: 'inventory',
            };
            const links = [
                {
                    name: this.translateService.getString('Document Dashboard'),
                    hasFeature: true,
                    count: '',
                    icon: this.pipeService.get('monIcons')(['MENUES', 'DASHBOARD']),
                    module: null,
                    link: 'dashboard',
                },
                {
                    name: this.translateService.getString('Content Policies'),
                    hasFeature: this.pipeService.get('activeFeature')('global_policy'),
                    count: this.page.policy_violation_count + this.page.policy_required_count + this.page.policy_search_count,
                    icon: this.pipeService.get('monIcons')(['MENUES', 'POLICIES']),
                    module: MON_MODULES.policies,
                    link: 'policy',
                },
                {
                    name: this.translateService.getString('Content Quality'),
                    hasFeature: true,
                    count: this.page.getQualityNotificationsCount(),
                    icon: this.pipeService.get('monIcons')(['MENUES', 'QA']),
                    module: MON_MODULES.qa,
                    link: 'qa',
                },
                {
                    name: this.translateService.getString('Accessibility'),
                    hasFeature: true,
                    count: this.page.accessibility_checks_with_errors_count,
                    show: this.sessionService.domain?.features.accessibility,
                    icon: this.pipeService.get('monIcons')(['MENUES', 'ACCESSIBILITY']),
                    module: MON_MODULES.accessibility,
                    link: 'accessibility',
                },
                {
                    name: this.translateService.getString('Data Privacy'),
                    hasFeature: true,
                    show: this.sessionService.domain?.features.data_protection,
                    icon: this.pipeService.get('monIcons')(['MENUES', 'DATA_PRIVACY']),
                    module: 'data-privacy',
                    link: 'data-privacy',
                },
                {
                    name: this.translateService.getString('Site Inventory'),
                    hasFeature: true,
                    icon: this.pipeService.get('monIcons')(['MENUES', 'INVENTORY']),
                    module: MON_MODULES.inventory,
                    link: 'inventory',
                },
            ];

            this.links = links.filter((link: any) => {
                return link.hasFeature && this.linkHasModule(link, user);
            });

            if (runSetupActive) {
                this.setupActive();
            }
        }).catch(() => {});
    }

    linkHasModule (link: any, user: any): boolean {
        return link.module === null || this.pipeService.get('hasAccess')(user, link.module);
    }
}
